'use client';
import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { quoteService, type QuoteRequest } from '@/lib/services/quoteService';
import { containerService } from '@/lib/services/containerService';
import { emailService } from '@/lib/services/emailService';
import { useSearchParams } from 'next/navigation';
import FormErrorBoundary from '@/components/common/FormErrorBoundary';
import Toast from '@/components/common/Toast';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Elements } from '@stripe/react-stripe-js';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  businessType: string;
  containerSize: string;
  location: string;
  timeline: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  businessType?: string;
  containerSize?: string;
  location?: string;
  timeline?: string;
}

// ─── Deposit Payment Inner Form ───────────────────────────────────────────────
interface DepositFormProps {
  depositAmount: number;
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onSkip: () => void;
}

const DepositPaymentForm = ({ depositAmount, onSuccess, onSkip }: DepositFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment submission failed');
        setIsProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/get-a-quote?deposit=success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed. Please check your card details and try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else {
        setErrorMessage('Payment processing incomplete. Please try again.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      console.error('Deposit payment error:', err);
      setErrorMessage(err?.message || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Amount Banner */}
      <div className="p-4 bg-primary/10 rounded-xl flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Reservation Deposit</p>
          <p className="text-xs text-muted-foreground mt-0.5">Applied toward your final order</p>
        </div>
        <p className="text-2xl font-bold text-primary">
          ${depositAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </p>
      </div>

      {/* Stripe Payment Element */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white">
        <PaymentElement options={{ layout: 'tabs', paymentMethodOrder: ['card'] }} />
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" role="alert">
          <Icon name="ExclamationCircleIcon" size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onSkip}
          disabled={isProcessing}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          Skip — I'll Pay Later
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {isProcessing ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={18} />
              Pay ${depositAmount} Deposit
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-center text-muted-foreground">
        🔒 Secured by Stripe. We never store your card details.
      </p>
    </form>
  );
};

// ─── Main Quote Form ──────────────────────────────────────────────────────────
const stripePromise = getStripe();

const QuoteFormSection = () => {
  const searchParams = useSearchParams();
  const modelCode = searchParams?.get('model');

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    businessType: '',
    containerSize: '',
    location: '',
    timeline: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [containerSizes, setContainerSizes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [submitError, setSubmitError] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);

  // Deposit payment state
  const [step, setStep] = useState<'form' | 'deposit'>('form');
  const [depositClientSecret, setDepositClientSecret] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState<number>(500);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState<string>('');
  const [depositPaid, setDepositPaid] = useState(false);

  const businessTypes = [
    'Pizza Restaurant',
    'Taco Stand',
    'Burger Joint',
    'Ice Cream Shop',
    'Coffee Shop',
    'Beverage Bar',
    'Other Food Service',
  ];

  const timelines = [
    'As soon as possible',
    'Within 1-2 months',
    'Within 3-6 months',
    'Just exploring options',
  ];

  useEffect(() => {
    const fetchContainerModels = async () => {
      try {
        const models = await containerService.getAll();
        const sizes = models.map(
          (model) => `${model.name} - $${model.price.toLocaleString()}`
        );
        setContainerSizes(sizes);

        if (modelCode) {
          const selectedModel = models.find((m) => m.code === modelCode);
          if (selectedModel) {
            setFormData((prev) => ({
              ...prev,
              containerSize: `${selectedModel.name} - $${selectedModel.price.toLocaleString()}`,
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load container models:', error);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchContainerModels();
  }, [modelCode]);

  useEffect(() => {
    if (loadingModels) return;

    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));

    return () => observer?.disconnect();
  }, [loadingModels]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s()+-]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'fullName':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!validatePhone(value)) return 'Please enter a valid phone number (at least 10 digits)';
        return '';
      case 'businessType':
        if (!value) return 'Please select a business type';
        return '';
      case 'containerSize':
        if (!value) return 'Please select a container model';
        return '';
      case 'location':
        if (!value.trim()) return 'Location is required';
        if (value.trim().length < 3) return 'Please enter a valid location';
        return '';
      case 'timeline':
        if (!value) return 'Please select a timeline';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: '' });
    }
    setSubmitError('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors({ ...errors, [name]: error });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const fullNameError = validateField('fullName', formData.fullName);
    if (fullNameError) newErrors.fullName = fullNameError;
    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;
    const phoneError = validateField('phone', formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    const businessTypeError = validateField('businessType', formData.businessType);
    if (businessTypeError) newErrors.businessType = businessTypeError;
    const containerSizeError = validateField('containerSize', formData.containerSize);
    if (containerSizeError) newErrors.containerSize = containerSizeError;
    const locationError = validateField('location', formData.location);
    if (locationError) newErrors.location = locationError;
    const timelineError = validateField('timeline', formData.timeline);
    if (timelineError) newErrors.timeline = timelineError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitQuoteToBackend = async (data: FormData) => {
    const quoteData: QuoteRequest = {
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
      businessType: data.businessType,
      containerSize: data.containerSize,
      location: data.location,
      timeline: data.timeline,
      message: data.message,
    };

    const success = await quoteService.createQuote(quoteData);
    if (!success) throw new Error('Failed to submit quote request');

    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    try {
      await fetch('/api/send-quote-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
          businessType: data.businessType,
          containerSize: data.containerSize,
          location: data.location,
          timeline: data.timeline,
          message: data.message,
          adminDashboardUrl: `${origin}/admin/leads`,
        }),
      });
    } catch (emailError) {
      console.log('Quote notification email failed:', emailError);
    }

    await emailService.sendAdminQuoteNotification({
      adminEmail: process.env.NEXT_PUBLIC_ADMIN_EMAIL || 'admin@containersbytakeazip.com',
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
      businessType: data.businessType,
      containerSize: data.containerSize,
      location: data.location,
      timeline: data.timeline,
      message: data.message,
      adminDashboardUrl: `${origin}/admin/leads`,
    });

    try {
      await fetch('/api/send-customer-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: data.email,
          name: data.fullName,
          type: 'quote',
          details: {
            businessType: data.businessType,
            containerSize: data.containerSize,
            location: data.location,
            timeline: data.timeline,
          },
        }),
      });
    } catch (emailError) {
      console.log('Customer confirmation email failed:', emailError);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await submitQuoteToBackend(formData);
      setSubmittedData({ ...formData });

      // Create deposit payment intent and move to deposit step
      setDepositLoading(true);
      try {
        const res = await fetch('/api/create-quote-deposit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: formData.fullName,
            email: formData.email,
            containerSize: formData.containerSize,
            location: formData.location,
          }),
        });
        const data = await res.json();
        if (data.clientSecret) {
          setDepositClientSecret(data.clientSecret);
          setDepositAmount(data.depositAmount || 500);
          setStep('deposit');
        } else {
          // If deposit setup fails, just show success
          finishSuccess();
        }
      } catch {
        // Deposit setup failed — still show success for the quote
        finishSuccess();
      } finally {
        setDepositLoading(false);
      }
    } catch (error) {
      console.error('Quote submission failed:', error);
      setSubmitError('An error occurred while submitting your quote. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const finishSuccess = () => {
    setShowToast(true);
    setShowModal(true);
    setIsSuccess(true);
    setStep('form');
    setDepositClientSecret(null);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      businessType: '',
      containerSize: '',
      location: '',
      timeline: '',
      message: '',
    });
    setErrors({});
  };

  const handleDepositSuccess = (paymentIntent: PaymentIntent) => {
    setDepositPaid(true);
    finishSuccess();
  };

  const handleDepositSkip = () => {
    finishSuccess();
  };

  if (loadingModels) {
    return (
      <section className="py-32 bg-background min-h-screen">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <Icon name="ArrowPathIcon" size={40} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading quote form...</p>
          </div>
        </div>
      </section>
    );
  }

  // ── Deposit Step ──────────────────────────────────────────────────────────
  if (step === 'deposit' && depositClientSecret) {
    return (
      <section className="py-16 sm:py-24 bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-10">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Icon name="CheckIcon" size={16} className="text-primary-foreground" />
                </div>
                <span className="text-sm font-medium text-primary">Quote Submitted</span>
              </div>
              <div className="w-8 h-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-sm font-bold text-primary-foreground">2</span>
                </div>
                <span className="text-sm font-medium text-foreground">Reserve Your Spot</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold font-montserrat text-foreground mb-3">
              Secure Your Container Order
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
              Your quote request was submitted! Pay a <strong>${depositAmount} refundable deposit</strong> now to reserve your container and lock in your spot.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: 'ShieldCheckIcon', label: 'Priority Processing' },
              { icon: 'ArrowPathIcon', label: 'Fully Refundable' },
              { icon: 'BoltIcon', label: 'Lock In Your Price' },
            ].map(({ icon, label }) => (
              <div key={label} className="text-center p-3 bg-primary/5 rounded-xl">
                <Icon name={icon as any} size={20} className="text-primary mx-auto mb-1" />
                <p className="text-xs font-medium text-foreground">{label}</p>
              </div>
            ))}
          </div>

          {/* Payment Card */}
          <div className="container-card p-6 sm:p-8">
            {depositLoading ? (
              <div className="text-center py-8">
                <Icon name="ArrowPathIcon" size={32} className="animate-spin text-primary mx-auto" />
                <p className="mt-3 text-sm text-muted-foreground">Setting up secure payment...</p>
              </div>
            ) : depositError ? (
              <div className="text-center py-8">
                <p className="text-sm text-red-600 mb-4">{depositError}</p>
                <button onClick={handleDepositSkip} className="text-sm text-primary underline">
                  Continue without deposit
                </button>
              </div>
            ) : (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: depositClientSecret,
                  appearance: { theme: 'stripe', variables: { borderRadius: '12px' } },
                }}
              >
                <DepositPaymentForm
                  depositAmount={depositAmount}
                  onSuccess={handleDepositSuccess}
                  onSkip={handleDepositSkip}
                />
              </Elements>
            )}
          </div>

          <p className="text-xs text-center text-muted-foreground mt-4">
            The deposit is fully refundable if we cannot fulfill your order. It will be applied toward your final payment.
          </p>
        </div>

        {/* Toast */}
        <Toast
          message="Quote submitted! Complete your deposit to reserve your container."
          type="success"
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      </section>
    );
  }

  // ── Quote Form Step ───────────────────────────────────────────────────────
  return (
    <section className="py-16 sm:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10">
        {/* Section Header */}
        <div className="reveal text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-montserrat text-foreground mb-4">
            Get Your Custom Quote
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Fill out the form below and we'll provide you with a detailed quote within 24 hours.
          </p>
        </div>

        {/* Form */}
        <div className="reveal container-card p-6 sm:p-8 md:p-12">
          <FormErrorBoundary formName="Quote Request Form">
            {isSubmitting && (
              <div className="sr-only" aria-live="assertive" aria-atomic="true">
                Submitting your quote request, please wait...
              </div>
            )}
            {isSuccess && (
              <div className="sr-only" aria-live="assertive" aria-atomic="true">
                Success! Your quote request has been submitted. We'll contact you within 24 hours.
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-5 sm:space-y-6"
              noValidate
              aria-label="Quote request form"
            >
              {submitError && (
                <div
                  className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
                  role="alert"
                  aria-live="assertive"
                >
                  <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              {/* Deposit Badge */}
              <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-xl">
                <Icon name="CreditCardIcon" size={20} className="text-primary flex-shrink-0" />
                <p className="text-sm text-foreground">
                  <span className="font-semibold">Reserve with a $500 deposit</span>
                  <span className="text-muted-foreground"> — optional after submission. Fully refundable.</span>
                </p>
              </div>

              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                  Full Name <span className="text-red-500" aria-label="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                    errors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Your name"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.fullName}
                  aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                />
                {errors.fullName && (
                  <p id="fullName-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email Address <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                      errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="your@email.com"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Phone Number <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                      errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="(123) 456-7890"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Business Type & Container Size */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-foreground mb-2">
                    Business Type <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] bg-white ${
                      errors.businessType ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.businessType}
                    aria-describedby={errors.businessType ? 'businessType-error' : undefined}
                  >
                    <option value="">Select business type</option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.businessType && (
                    <p id="businessType-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.businessType}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="containerSize" className="block text-sm font-medium text-foreground mb-2">
                    Container Model <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <select
                    id="containerSize"
                    name="containerSize"
                    value={formData.containerSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={loadingModels}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all disabled:opacity-50 touch-manipulation min-h-[48px] bg-white ${
                      errors.containerSize ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.containerSize}
                    aria-describedby={errors.containerSize ? 'containerSize-error' : undefined}
                  >
                    <option value="">
                      {loadingModels ? 'Loading models...' : 'Select container model'}
                    </option>
                    {containerSizes.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  {errors.containerSize && (
                    <p id="containerSize-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.containerSize}
                    </p>
                  )}
                </div>
              </div>

              {/* Location & Timeline */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
                    Location <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                      errors.location ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="City, State"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.location}
                    aria-describedby={errors.location ? 'location-error' : undefined}
                  />
                  {errors.location && (
                    <p id="location-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.location}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-foreground mb-2">
                    Timeline <span className="text-red-500" aria-label="required">*</span>
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] bg-white ${
                      errors.timeline ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.timeline}
                    aria-describedby={errors.timeline ? 'timeline-error' : undefined}
                  >
                    <option value="">Select timeline</option>
                    {timelines.map((timeline) => (
                      <option key={timeline} value={timeline}>{timeline}</option>
                    ))}
                  </select>
                  {errors.timeline && (
                    <p id="timeline-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.timeline}
                    </p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none touch-manipulation"
                  placeholder="Tell us more about your project..."
                  aria-label="Additional details about your project"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || loadingModels}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 sm:py-5 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[56px] text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-primary/30"
                aria-label={isSubmitting ? 'Submitting quote request' : 'Submit quote request'}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" aria-hidden="true" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Quote Request</span>
                    <Icon name="PaperAirplaneIcon" size={20} aria-hidden="true" />
                  </>
                )}
              </button>
            </form>
          </FormErrorBoundary>
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={depositPaid
          ? "Deposit paid & quote submitted! We'll send your proposal within 24 hours." :"Quote request submitted successfully! We'll send you a detailed proposal within 24 hours."}
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={depositPaid ? "Deposit Paid & Quote Submitted!" : "Quote Request Submitted!"}
        message={depositPaid
          ? "Your $500 deposit has been received and your container is reserved. We'll send you a detailed proposal within 24 hours." :"Thank you for your interest. We've received your quote request and will send you a detailed proposal within 24 hours."}
        details={submittedData ? [
          { label: 'Name', value: submittedData.fullName },
          { label: 'Email', value: submittedData.email },
          { label: 'Phone', value: submittedData.phone },
          { label: 'Business Type', value: submittedData.businessType },
          { label: 'Container Model', value: submittedData.containerSize },
          { label: 'Location', value: submittedData.location },
          { label: 'Timeline', value: submittedData.timeline },
          ...(depositPaid ? [{ label: 'Deposit', value: '$500.00 paid ✓' }] : []),
        ] : []}
        confirmationNumber={`QTE-${Date.now().toString().slice(-8)}`}
      />
    </section>
  );
};

export default QuoteFormSection;